import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import {
  AUTOPLAY_DELAY_DEFAULT,
  AUTOPLAY_DELAY_MIN,
  MEMORIAL_BADGE,
} from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';
import { SEO_TITLE_LENGTH } from 'volto-ministerio-publico-federal/widgets/CharCountWidget';
import { requireAltWhenOwnImage } from 'volto-ministerio-publico-federal/helpers/Image/altRequired';

const messages = defineMessages({
  titleBlock: {
    id: 'mpf_destaques_carrossel_block',
    defaultMessage: 'Destaques — carrossel (capa)',
  },
  title: {
    id: 'mpf_destaques_carrossel_title',
    defaultMessage: 'Título da faixa',
  },
  titleHelp: {
    id: 'mpf_destaques_carrossel_title_help',
    defaultMessage: 'Em branco, usa "Destaques".',
  },
  list: { id: 'mpf_destaques_carrossel_list', defaultMessage: 'Destaques' },
  listHelp: {
    id: 'mpf_destaques_carrossel_list_help',
    defaultMessage:
      'Um destaque por vez, na ordem da lista. Todos levam a pílula "{badge}".',
  },
  item: { id: 'mpf_destaques_carrossel_item', defaultMessage: 'Destaque' },
  itemTitle: {
    id: 'mpf_destaques_carrossel_item_title',
    defaultMessage: 'Título',
  },
  itemTitleHelp: {
    id: 'mpf_destaques_carrossel_item_title_help',
    defaultMessage:
      'Até 60 caracteres é o que o Google costuma exibir sem cortar. Acima disso o título ainda aparece inteiro na capa.',
  },
  itemDescription: {
    id: 'mpf_destaques_carrossel_item_description',
    defaultMessage: 'Descrição',
  },
  itemDescriptionHelp: {
    id: 'mpf_destaques_carrossel_item_description_help',
    defaultMessage: 'Exibida em até três linhas, abaixo do título.',
  },
  itemHref: {
    id: 'mpf_destaques_carrossel_item_href',
    defaultMessage: 'Conteúdo',
  },
  itemHrefHelp: {
    id: 'mpf_destaques_carrossel_item_href_help',
    defaultMessage:
      'Destino do botão "Saiba mais": uma página do site ou um endereço externo. Ao escolher um conteúdo, o título é preenchido com o dele — título já editado não é sobrescrito. Sem destino, o botão não aparece.',
  },
  itemExternal: {
    id: 'mpf_destaques_carrossel_item_external',
    defaultMessage: 'Abrir em nova aba',
  },
  fieldsetImage: {
    id: 'mpf_destaques_carrossel_fieldset_image',
    defaultMessage: 'Imagem',
  },
  itemImage: {
    id: 'mpf_destaques_carrossel_item_image',
    defaultMessage: 'Imagem',
  },
  itemImageHelp: {
    id: 'mpf_destaques_carrossel_item_image_help',
    defaultMessage:
      'Arraste um arquivo, escolha uma imagem do site ou cole uma URL. Ela fica ao fundo, sob um véu escuro — prefira fotos com pouco texto.',
  },
  itemAlt: {
    id: 'mpf_destaques_carrossel_item_alt',
    defaultMessage: 'Texto alternativo',
  },
  itemAltHelp: {
    id: 'mpf_destaques_carrossel_item_alt_help',
    defaultMessage: 'Descreva o conteúdo da imagem para leitores de tela.',
  },
  fieldsetTransicao: {
    id: 'mpf_destaques_carrossel_fieldset_transicao',
    defaultMessage: 'Transição',
  },
  autoplay: {
    id: 'mpf_destaques_carrossel_autoplay',
    defaultMessage: 'Passar os destaques sozinho',
  },
  autoplayHelp: {
    id: 'mpf_destaques_carrossel_autoplay_help',
    defaultMessage:
      'Desligado, o destaque só muda quando a pessoa clica na seta ou no ponto. Ligado, a troca pausa ao passar o mouse e ao focar pelo teclado, e não roda para quem pediu menos animação no sistema.',
  },
  autoplayDelay: {
    id: 'mpf_destaques_carrossel_autoplay_delay',
    defaultMessage: 'Segundos em cada destaque',
  },
  autoplayDelayHelp: {
    id: 'mpf_destaques_carrossel_autoplay_delay_help',
    defaultMessage: 'Só vale com a troca automática ligada. Mínimo de 3.',
  },
});

/**
 * Campos de um slide.
 *
 * Sem campo de categoria, diferente do mosaico de iniciativas: a pílula é
 * sempre a do Memorial MPF, decisão de design e não escolha editorial.
 */
const destaqueCarrosselItemSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.item),
  addMessage: intl.formatMessage(messages.item),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['title', 'description', 'href', 'external'],
    },
    {
      id: 'image',
      title: intl.formatMessage(messages.fieldsetImage),
      fields: ['image', 'alt'],
    },
  ],
  properties: {
    // Contador em vez de corte: o título aparece inteiro na capa, então o
    // tamanho é decisão editorial com aviso na hora de escrever. Ver
    // `widgets/CharCountWidget`.
    title: {
      title: intl.formatMessage(messages.itemTitle),
      description: intl.formatMessage(messages.itemTitleHelp),
      widget: 'charCount',
      recommendedLength: SEO_TITLE_LENGTH,
    },
    description: {
      title: intl.formatMessage(messages.itemDescription),
      description: intl.formatMessage(messages.itemDescriptionHelp),
      widget: 'textarea',
    },
    href: {
      title: intl.formatMessage(messages.itemHref),
      description: intl.formatMessage(messages.itemHrefHelp),
      widget: 'object_browser',
      mode: 'link',
      // Sem `image_scales`/`image_field` de propósito: aqui a foto é fundo de
      // um véu colorido, escolha editorial que a imagem do conteúdo
      // referenciado raramente atende.
      selectedItemAttrs: ['@id', '@type', 'Title', 'Description'],
      allowExternals: true,
      initialPath: '/',
    },
    external: {
      title: intl.formatMessage(messages.itemExternal),
      type: 'boolean',
    },
    // `image` (e não `object_browser`): este widget navega no site, aceita
    // arrastar um arquivo para subir e também colar uma URL.
    image: {
      title: intl.formatMessage(messages.itemImage),
      description: intl.formatMessage(messages.itemImageHelp),
      widget: 'image',
    },
    alt: {
      title: intl.formatMessage(messages.itemAlt),
      description: intl.formatMessage(messages.itemAltHelp),
    },
  },
  // `alt` só é obrigatório quando há imagem — o `schemaExtender` de `items`
  // cuida disso. Sem imagem no slide, não há o que descrever.
  required: ['title'],
});

/**
 * Schema do bloco "Destaques — carrossel".
 *
 * Não confundir com `mpfDestaques`, que é a GRADE de notícias da primeira
 * faixa da capa. São dois blocos distintos.
 *
 * `activeObject`/`setActiveObject` vêm do `Edit.tsx` e entram no field spec de
 * `items` — é o contrato que o `ObjectListWidget` do core espera para aceitar
 * um índice controlado de fora, e é o que faz o lápis de um slide abrir aquele
 * item.
 */
export const mpfDestaquesCarrosselSchema = ({
  intl,
  activeObject,
  setActiveObject,
}: {
  intl: IntlShape;
  activeObject?: number;
  setActiveObject?: (index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['title', 'items'],
    },
    {
      id: 'transicao',
      title: intl.formatMessage(messages.fieldsetTransicao),
      fields: ['autoplay', 'autoplayDelay'],
    },
  ],
  properties: {
    title: {
      title: intl.formatMessage(messages.title),
      description: intl.formatMessage(messages.titleHelp),
      placeholder: 'Destaques',
    },
    // Chave liga/desliga em vez da caixa de seleção do core: a opção muda o
    // comportamento da página, e o estado precisa se ler de relance.
    autoplay: {
      title: intl.formatMessage(messages.autoplay),
      description: intl.formatMessage(messages.autoplayHelp),
      type: 'boolean',
      widget: 'toggle',
      default: false,
    },
    autoplayDelay: {
      title: intl.formatMessage(messages.autoplayDelay),
      description: intl.formatMessage(messages.autoplayDelayHelp),
      type: 'number',
      minimum: AUTOPLAY_DELAY_MIN,
      default: AUTOPLAY_DELAY_DEFAULT,
    },
    items: {
      title: intl.formatMessage(messages.list),
      description: intl.formatMessage(messages.listHelp, {
        badge: MEMORIAL_BADGE.label,
      }),
      widget: 'object_list',
      schema: destaqueCarrosselItemSchema({ intl }),
      schemaExtender: requireAltWhenOwnImage,
      activeObject,
      setActiveObject,
    },
  },
  required: [],
});

export default mpfDestaquesCarrosselSchema;
